/**
 * Shared bits for the AngularJS chart controllers: tooltip state and
 * positioning (relative to the `.xac-chart` container), opt-in responsive
 * width measurement (`autoWidth`), the tooltip and visually-hidden data-table
 * template fragments, and the per-instance gradient-salt counter.
 *
 * All geometry, formatting, palette and CSS logic lives in @xait/charts-core;
 * controllers here only hold interaction state (hidden-series set, hover) and
 * re-run the core compute/tooltip functions when inputs change.
 */
import { TOOLTIP_OFFSET } from '@xait/charts-core';
import type { XaTooltipContent, XaTooltipRow } from '@xait/charts-core';

/** Shape of an AngularJS `'&'` output binding: invoked as `cb({ $event })`. */
export type XaCallback<E> = (locals: { $event: E }) => void;

/** Tooltip view state: container-relative position + content. */
export interface XaTipState {
  x: number;
  y: number;
  title?: string;
  rows: XaTooltipRow[];
}

let gradientSaltCounter = 0;

/** Unique-per-component-instance salt so SVG gradient ids never collide. */
export function nextGradientSalt(): string {
  gradientSaltCounter += 1;
  return 'ng' + gradientSaltCounter;
}

/**
 * The single tooltip div per chart. It is `position: fixed` and
 * `pointer-events: none` (both from the shared core CSS), so it floats above
 * everything and is never clipped by an ancestor's `overflow`.
 */
export const XAC_TIP_TEMPLATE = `<div class="xac-tip" ng-if="$ctrl.tip" ng-style="{ left: $ctrl.tip.x + 'px', top: $ctrl.tip.y + 'px' }">
    <div class="xac-tip-title" ng-if="$ctrl.tip.title">{{$ctrl.tip.title}}</div>
    <div class="xac-tip-row" ng-repeat="row in $ctrl.tip.rows track by $index">
      <span class="xac-swatch" ng-if="row.color" ng-style="{ background: row.color }"></span>
      <span class="xac-tip-label">{{row.label}}</span>
      <span class="xac-tip-value">{{row.value}}</span>
    </div>
  </div>`;

/** Visually-hidden data-table fallback for categorical charts (a11y). */
export const XAC_CATEGORICAL_TABLE = `<table class="xac-sr-only" ng-if="$ctrl.data">
    <thead>
      <tr>
        <th scope="col">Category</th>
        <th scope="col" ng-repeat="s in $ctrl.data.series track by $index">{{s.name}}</th>
      </tr>
    </thead>
    <tbody>
      <tr ng-repeat="c in $ctrl.data.categories track by $index" ng-init="ci = $index">
        <th scope="row">{{c}}</th>
        <td ng-repeat="s in $ctrl.data.series track by $index">{{s.values[ci] || 0}}</td>
      </tr>
    </tbody>
  </table>`;

/** Visually-hidden data-table fallback for slice charts (donut / pie). */
export const XAC_SLICE_TABLE = `<table class="xac-sr-only" ng-if="$ctrl.data">
    <tbody>
      <tr ng-repeat="d in $ctrl.data track by $index">
        <th scope="row">{{d.label}}</th>
        <td>{{d.value}}</td>
      </tr>
    </tbody>
  </table>`;

/**
 * Base controller: owns the tooltip state. Resolves pointer coordinates
 * relative to the `.xac-chart` container for axis snapping, while the tooltip
 * itself (`position: fixed`) anchors to viewport coordinates.
 */
export class XaChartBaseController implements ng.IController {
  /** Current tooltip, or null when hidden. */
  tip: XaTipState | null = null;

  /**
   * Opt-in responsive width binding. When truthy the chart re-flows to the
   * measured width of its `.xac-chart` container (see `effectiveWidth`); when
   * falsy/unset the chart keeps its explicit (pixel) `width`. Width-based
   * components expose this as the `auto-width` attribute.
   */
  autoWidth?: boolean;

  /** Latest measured container width in px (null until first measure). */
  protected measuredWidth: number | null = null;

  private containerEl: HTMLElement | null = null;

  /** Live observer on the container; created by `observeAutoWidth`. */
  private resizeObserver: ResizeObserver | null = null;

  /** Floor for the measured width so charts never collapse to nothing. */
  private static readonly MIN_AUTO_WIDTH = 160;

  constructor(protected readonly $element: ng.IAugmentedJQuery) {}

  $postLink(): void {
    const host = this.$element[0];
    this.containerEl = (host.querySelector('.xac-chart') as HTMLElement | null) ?? host;
  }

  /**
   * The width to hand the core layout: the measured container width when
   * `autoWidth` is on (and a measurement exists), otherwise the explicit
   * `width`. Keeps the pixel-exact default intact when `autoWidth` is unset.
   */
  protected effectiveWidth(explicit: number): number {
    return this.autoWidth && this.measuredWidth != null ? this.measuredWidth : explicit;
  }

  /**
   * Start measuring the `.xac-chart` container with a `ResizeObserver` and call
   * `redraw` whenever the (integer, min-floored) width changes while `autoWidth`
   * is on. Width is rounded and de-duplicated to avoid ResizeObserver feedback
   * loops. The first measurement runs synchronously so the initial render can
   * already use the real width. Components call this from `$postLink` (after
   * `super.$postLink()`) and pair it with `disconnectAutoWidth` in `$onDestroy`.
   */
  protected observeAutoWidth(redraw: () => void): void {
    const el = this.containerEl ?? this.$element[0];
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = (): void => {
      const w = Math.round(el.clientWidth);
      if (w <= 0) return;
      const next = Math.max(w, XaChartBaseController.MIN_AUTO_WIDTH);
      if (next === this.measuredWidth) return;
      this.measuredWidth = next;
      if (this.autoWidth) redraw();
    };
    measure();
    this.resizeObserver = new ResizeObserver(measure);
    this.resizeObserver.observe(el);
  }

  /** Stop measuring; call from each observing component's `$onDestroy`. */
  protected disconnectAutoWidth(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /** Pointer position relative to the chart container (`.xac-chart`), for axis snapping. */
  protected pointerPos(ev: MouseEvent): { x: number; y: number } {
    const el = this.containerEl ?? this.$element[0];
    const rect = el.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }

  /**
   * Show the tooltip at the cursor (+14/+6 offset from the core constant).
   * The tip is `position: fixed`, so it anchors to viewport coordinates
   * (`clientX/clientY`) to avoid being clipped by an ancestor's `overflow`.
   */
  protected showTip(ev: MouseEvent, content: XaTooltipContent): void {
    this.tip = {
      x: ev.clientX + TOOLTIP_OFFSET.x,
      y: ev.clientY + TOOLTIP_OFFSET.y,
      title: content.title,
      rows: content.rows,
    };
  }

  protected hideTip(): void {
    this.tip = null;
  }
}
